import {
	Button,
	Dimmer,
	type Job,
	Loader,
	TraitementLong,
} from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import { isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import type Simulation from '../../../../interfaces/simulation/simulation';
import {
	getRepriseRestesARealiser,
	repriseRestesARealiser,
} from '../../../../services/simulation/simulation';
import OutilTable from '../OutilTable';
import type { OutilTableRowProps } from '../OutilTableRow';

interface ResteARealiserProps {
	onValidate: (data?: unknown) => void;
	simulation: Simulation;
}

const ResteARealiser = ({ simulation, onValidate }: ResteARealiserProps) => {
	const [loadingData, setLoadingData] = React.useState<boolean>(true);
	const [loadingValidate, setLoadingValidate] = React.useState<boolean>(false);
	const [data, setData] = React.useState<OutilTableRowProps[]>([]);
	const [job, setJob] = React.useState<Job>();

	const handleFetchRestesARealiser = async () => {
		try {
			setLoadingData(false);
			const rar = await getRepriseRestesARealiser(simulation?.identifiant);

			const rarDepenseInvestissement = rar?.find(
				(r) => r.imp_sens === 'D' && r.cha_section === 'I',
			);
			const rarDepenseFonctionnement = rar?.find(
				(r) => r.imp_sens === 'D' && r.cha_section === 'F',
			);
			const rarRecetteInvestissement = rar?.find(
				(r) => r.imp_sens === 'R' && r.cha_section === 'I',
			);
			const rarRecetteFonctionnement = rar?.find(
				(r) => r.imp_sens === 'R' && r.cha_section === 'F',
			);

			const newData = [
				{
					positive: false,
					solde: rarDepenseInvestissement?.montant,
					title: "Restes à réaliser en dépense d'investissement",
				},
				{
					positive: true,
					solde: rarRecetteInvestissement?.montant,
					title: "Restes à réaliser en recette d'investissement",
				},
			];

			if (
				!isNil(rarDepenseFonctionnement) ||
				!isNil(rarRecetteFonctionnement)
			) {
				newData.push({
					positive: false,
					solde: rarDepenseFonctionnement?.montant,
					title: 'Restes à réaliser en dépense de fonctionnement',
				});

				newData.push({
					positive: true,
					solde: rarRecetteFonctionnement?.montant,
					title: 'Restes à réaliser en recette de fonctionnement',
				});
			}

			setData(newData);
		} catch (e) {
			toast.error(
				getErrorMessage(
					e,
					'Erreur lors de la récupération des restes à réaliser',
				),
			);
		}
	};

	const handleValidate = async () => {
		try {
			setLoadingValidate(true);
			const newJob = await repriseRestesARealiser(simulation?.identifiant);
			setJob(newJob);
		} catch (e) {
			setLoadingValidate(false);
			toast.error(
				getErrorMessage(e, 'Erreur lors de la recopie des restes à réaliser'),
			);
		}
	};

	const handleCloseJob = (finishedJob: Job) => {
		setJob(null);
		setLoadingValidate(false);

		if (finishedJob.status === 1) {
			// Le resultat liste les lignes APCP ignorees, lu par la modal
			onValidate?.(finishedJob.resultat);
		} else {
			toast.error(finishedJob.resultat);
		}
	};

	const handleJobError = () => {
		setJob(null);
		setLoadingValidate(false);
		toast.error('Erreur lors du traitement');
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'handleFetchRestesARealiser'
	React.useEffect(() => {
		handleFetchRestesARealiser();
	}, []);

	return loadingData ? (
		<Dimmer active inverted>
			<Loader size="massive" />
		</Dimmer>
	) : (
		<>
			<OutilTable data={data} />
			<div>
				<Button
					content="Reprendre les restes à réaliser"
					data-testid="confirmButton"
					disabled={loadingValidate}
					floated="right"
					loading={loadingValidate}
					onClick={handleValidate}
					positive
				/>
			</div>

			{loadingValidate && job && (
				<TraitementLong
					title="Reprise des restes à réaliser"
					identifiantJob={job?.identifiant}
					onFinish={handleCloseJob}
					onError={handleJobError}
				/>
			)}
		</>
	);
};

export default ResteARealiser;
